
/**
 * Incident Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class IncidentService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Incident listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch incident list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch incident');
        }
    }

    async getDetail(id) {
        try {
            const response = await withRetry(
                () => this.api.getDetail(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch incident detail');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Incident oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create incident');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Incident güncellendi',
                pendingChangeId: response.pendingChangeId || response.data?.pendingChangeId,
                approvalStatus: response.approvalStatus || response.data?.approvalStatus,
                merged: response.merged === true || response.data?.merged === true,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update incident');
        }
    }

    async submitPendingUpdate(id) {
        try {
            const response = await this.api.submitPendingUpdate(id);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
                pendingChangeId: response.data?.pendingChangeId,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit pending update');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Incident silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete incident');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: getMonitoring
     */
    async getMonitoring(data = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/monitoring`, { params: data });
            const body = response.data;
            return {
                success: true,
                data: body?.data ?? body,
                message: body?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute getMonitoring');
        }
    }

    async checkCodeAvailability(params = {}) {
        try {
            const response = await this.api.checkCodeAvailability(params);
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to check incident code availability');
        }
    }

    async getRelatedActions(id, params = {}) {
        return this._paginatedSubResource(id, 'actions', params, 'Failed to fetch related actions');
    }

    async getLinkableActions(id, params = {}) {
        return this._paginatedSubResource(id, 'actions/linkable', params, 'Failed to fetch linkable actions');
    }

    async linkActions(id, actionIds, extra = {}) {
        return this._mutationSubResource(id, 'actions', { actionIds, ...extra }, 'Failed to link actions');
    }

    async unlinkActions(id, actionIds) {
        return this._mutationSubResource(id, 'actions/unlink', { actionIds }, 'Failed to unlink actions');
    }

    async unlinkAction(id, actionId) {
        try {
            const response = await this.api.unlinkAction(id, actionId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink action');
        }
    }

    async reorderActions(id, payload = {}) {
        try {
            const response = await this.api.reorderActions(id, payload);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to reorder linked actions');
        }
    }

    async getRelatedTickets(id, params = {}) {
        return this._paginatedSubResource(id, 'tickets', params, 'Failed to fetch related tickets');
    }

    async getLinkableTickets(id, params = {}) {
        return this._paginatedSubResource(id, 'tickets/linkable', params, 'Failed to fetch linkable tickets');
    }

    async linkTickets(id, ticketIds) {
        return this._mutationSubResource(id, 'tickets', { ticketIds }, 'Failed to link tickets');
    }

    async unlinkTickets(id, ticketIds) {
        return this._mutationSubResource(id, 'tickets/unlink', { ticketIds }, 'Failed to unlink tickets');
    }

    async unlinkTicket(id, ticketId) {
        try {
            const response = await this.api.unlinkTicket(id, ticketId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink ticket');
        }
    }

    async getRelatedProblems(id, params = {}) {
        return this._paginatedSubResource(id, 'problems', params, 'Failed to fetch related problems');
    }

    async getLinkableProblems(id, params = {}) {
        return this._paginatedSubResource(id, 'problems/linkable', params, 'Failed to fetch linkable problems');
    }

    async linkProblems(id, problemIds) {
        return this._mutationSubResource(id, 'problems', { problemIds }, 'Failed to link problems');
    }

    async unlinkProblems(id, problemIds) {
        return this._mutationSubResource(id, 'problems/unlink', { problemIds }, 'Failed to unlink problems');
    }

    async unlinkProblem(id, problemId) {
        try {
            const response = await this.api.unlinkProblem(id, problemId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink problem');
        }
    }

    async getRelatedChangeRequests(id, params = {}) {
        return this._paginatedSubResource(id, 'change-requests', params, 'Failed to fetch related change requests');
    }

    async getLinkableChangeRequests(id, params = {}) {
        return this._paginatedSubResource(id, 'change-requests/linkable', params, 'Failed to fetch linkable change requests');
    }

    async linkChangeRequests(id, changeRequestIds) {
        return this._mutationSubResource(id, 'change-requests', { changeRequestIds }, 'Failed to link change requests');
    }

    async unlinkChangeRequests(id, changeRequestIds) {
        return this._mutationSubResource(id, 'change-requests/unlink', { changeRequestIds }, 'Failed to unlink change requests');
    }

    async unlinkChangeRequest(id, changeRequestId) {
        try {
            const response = await this.api.unlinkChangeRequest(id, changeRequestId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink change request');
        }
    }

    async getComments(id, params = {}) {
        try {
            const response = await this.api.getComments(id, params);
            return { success: true, data: response.data, message: response.message, pagination: response.pagination };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch comments');
        }
    }

    async addComment(id, data) {
        try {
            const response = await this.api.addComment({ incident: id, ...data });
            return { success: true, data: response.data, message: response.message, pagination: response.pagination };
        } catch (error) {
            throw this._handleError(error, 'Failed to add comment');
        }
    }

    async updateComment(_id, commentId, data) {
        try {
            const response = await this.api.updateComment(commentId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update comment');
        }
    }

    async deleteComment(_id, commentId) {
        try {
            const response = await this.api.deleteComment(commentId);
            return { success: true, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete comment');
        }
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await this.api.getPartyTemplate(params);
            return { success: true, data: response.data, partyTemplate: response.partyTemplate, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party template');
        }
    }

    async getParty(id) {
        try {
            const response = await this.api.getParty(id);
            return { success: true, data: response.data, partyTemplate: response.partyTemplate, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party');
        }
    }

    async syncParty(id, data) {
        try {
            const response = await this.api.syncParty(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync party');
        }
    }

    async getAttachments(id) {
        try {
            const response = await this.api.getAttachments(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch attachments');
        }
    }

    async uploadAttachment(id, file, category = 'attachment', optionsOrCallback) {
        try {
            const response = await this.api.uploadAttachment(id, file, category, optionsOrCallback);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to upload attachment');
        }
    }

    async deleteAttachment(id, attachmentId) {
        try {
            const response = await this.api.deleteAttachment(id, attachmentId);
            return { success: true, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete attachment');
        }
    }

    async submit(id) {
        return this._lifecycleMutation(() => this.api.submit(id), 'Failed to submit incident');
    }

    async assign(id, data) {
        return this._lifecycleMutation(() => this.api.assign(id, data), 'Failed to assign incident');
    }

    async claim(id) {
        return this._lifecycleMutation(() => this.api.claim(id), 'Failed to claim incident');
    }

    async acceptAssignment(id) {
        return this._lifecycleMutation(() => this.api.acceptAssignment(id), 'Failed to accept assignment');
    }

    async rejectAssignment(id, data) {
        return this._lifecycleMutation(() => this.api.rejectAssignment(id, data), 'Failed to reject assignment');
    }

    async resolve(id, data = {}) {
        return this._lifecycleMutation(() => this.api.resolve(id, data), 'Failed to resolve incident');
    }

    async close(id, data = {}) {
        return this._lifecycleMutation(() => this.api.close(id, data), 'Failed to close incident');
    }

    async getWorkflowTemplateOptions(params = {}) {
        try {
            const response = await this.api.getWorkflowTemplateOptions(params);
            const body = response?.data != null ? response : { data: response?.data ?? response };
            return {
                success: true,
                data: body.data ?? body,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch workflow template options');
        }
    }

    async _lifecycleMutation(fn, errorMessage) {
        try {
            const response = await fn();
            const body = response?.data != null ? response : { data: response?.data ?? response };
            return {
                success: body.success !== false,
                data: body.data,
                message: body.message,
                pending: body.pending === true,
            };
        } catch (error) {
            throw this._handleError(error, errorMessage);
        }
    }

    async _paginatedSubResource(id, pathSuffix, params, errorMessage) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/${pathSuffix}`, { params });
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                meta: body.pagination || body.meta || {},
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, errorMessage);
        }
    }

    async _mutationSubResource(id, pathSuffix, payload, errorMessage) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/${pathSuffix}`, payload);
            const body = response.data || response;
            return {
                success: body.success !== false,
                data: body.data,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, errorMessage);
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
